import React from 'react';
import styled, { css } from 'styled-components';
import { token, spaceMixin, mediaMixin, TextButton, Paragraph } from '@zillow/constellation';
import { shouldShowContactAgentForm } from '@zillow/off-market-contact-agent-form';
import type { Property, AbTests } from '../types/@zillow-types/pongo';
import {
    trackFSBOClick,
    trackSellingTipsClick,
    trackContactAgentFormLinkClick,
} from '../utils/gaUtils';
import { useYieldCallback } from '@zillow/yield-callback';

const StyledDiv = styled.div`
    background-color: ${token('colors.white')};
    border-style: solid;
    border-radius: 3px;
    border-width: 1px;
    border-color: ${token('colors.gray300')};
    padding: ${spaceMixin(3)} ${spaceMixin(2)};

    ${mediaMixin(
        'md_lte',
        css`
            padding: ${spaceMixin(1.25)};
        `
    )};
`;

interface SellerLinksProps {
    property: Property;
    abTests: AbTests;
    setShowContactAgentForm: (newState: boolean) => void;
    surfaceId: string;
    placementId: string;
}

const SellerLinks = ({
    property,
    abTests,
    setShowContactAgentForm,
    surfaceId,
    placementId,
}: SellerLinksProps) => {
    // eslint-disable-next-line zillow/@typescript-eslint/no-unsafe-assignment
    // @ts-expect-error property type is different in off-market-contact-agent package
    // fix in STL-4217
    const showContactAgentFormLink = shouldShowContactAgentForm(property, abTests);
    const onFSBOClick = useYieldCallback(() => {
        trackFSBOClick(surfaceId, placementId);
    });
    const onSellingTipsClick = useYieldCallback(() => {
        trackSellingTipsClick(surfaceId, placementId);
    });
    return (
        <StyledDiv data-testid="seller-links">
            <Paragraph fontType="h6">Thinking of selling? Start here</Paragraph>
            {showContactAgentFormLink && (
                <React.Fragment>
                    <TextButton
                        marginTop="sm"
                        fontType="bodyHeading"
                        onClick={() => {
                            trackContactAgentFormLinkClick(surfaceId, placementId);
                            setShowContactAgentForm(true);
                        }}
                        data-testid="professional-valuation-link"
                    >
                        Get a professional valuation
                    </TextButton>
                    <Paragraph>
                        Connect with a local agent to see what your home could sell for.
                    </Paragraph>
                </React.Fragment>
            )}
            <TextButton
                marginTop="sm"
                fontType="bodyHeading"
                as="a"
                onClick={onSellingTipsClick}
                href="/sellers-guide"
                data-testid="selling-tips-link"
            >
                Get selling tips
            </TextButton>
            <Paragraph>
                Check out our Sellers Guide for timelines, tips and advice on selling your home.
            </Paragraph>
            <TextButton
                marginTop="sm"
                fontType="bodyHeading"
                as="a"
                onClick={onFSBOClick}
                href="/for-sale-by-owner"
                data-testid="for-sale-by-owner-link"
            >
                List for sale by owner
            </TextButton>
            <Paragraph>Post a listing for free, including video and unlimited photos.</Paragraph>
        </StyledDiv>
    );
};

export default SellerLinks;
